import React, { useState } from "react";
import { api, formatApiError, whatsappLink } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, Send } from "lucide-react";

export default function EnquiryForm({ defaultService = "general_enquiry", title = "Enquire Now", sourcePage, propertyId, compact = false }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", message: "", location: "", budget: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        ...form,
        service_type: defaultService,
        source_page: sourcePage,
        property_id: propertyId,
      });
      toast.success("Enquiry submitted. Our team will reach out shortly.");
      setForm({ name: "", phone: "", email: "", message: "", location: "", budget: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white border border-[#E2E8F0] rounded-lg px-3.5 py-2.5 text-sm text-[#1F2937] placeholder-[#94A3B8] focus:outline-none focus:border-[#183B66] focus:ring-2 focus:ring-[#183B66]/15 transition";

  return (
    <form onSubmit={submit} className="space-y-3.5" data-testid="enquiry-form">
      {!compact && <h3 className="font-bold text-xl text-[#101827] mb-1">{title}</h3>}
      <div className={`grid ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2"} gap-3`}>
        <input className={inputCls} placeholder="Full Name *" value={form.name} onChange={update("name")} data-testid="enquiry-name" />
        <input className={inputCls} placeholder="Phone *" value={form.phone} onChange={update("phone")} data-testid="enquiry-phone" />
      </div>
      <input className={inputCls} placeholder="Email (optional)" value={form.email} onChange={update("email")} data-testid="enquiry-email" />
      {!propertyId && (
        <div className="grid grid-cols-2 gap-3">
          <input className={inputCls} placeholder="Location of interest" value={form.location} onChange={update("location")} data-testid="enquiry-location" />
          <input className={inputCls} placeholder="Budget (e.g. 50L - 1Cr)" value={form.budget} onChange={update("budget")} data-testid="enquiry-budget" />
        </div>
      )}
      <textarea className={inputCls + " min-h-[90px]"} placeholder="How can we help?" value={form.message} onChange={update("message")} data-testid="enquiry-message" />
      <div className="flex flex-col sm:flex-row gap-2">
        <button type="submit" disabled={submitting} className="ae-btn-primary flex-1 justify-center" data-testid="enquiry-submit">
          <Send className="w-4 h-4" /> {submitting ? "Submitting..." : "Submit Enquiry"}
        </button>
        <a
          href={whatsappLink(`Hello Apurba Estate, I am ${form.name || "a prospective client"} and I would like to discuss ${defaultService.replace(/_/g, " ")}.`)}
          target="_blank"
          rel="noopener noreferrer"
          className="ae-btn-whatsapp justify-center"
        >
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </a>
      </div>
      <p className="text-[11px] text-[#64748B]">By submitting, you consent to Apurba Estate contacting you about your enquiry. See our Privacy Policy.</p>
    </form>
  );
}
